import React, {useEffect, useState} from "react";
import {Box, Grid, Paper,Snackbar, Typography} from '@material-ui/core';
import {useForm} from "react-hook-form";
import Button from "@material-ui/core/Button";
import addressData from '../../data/addressData.json'
import axiosInstance from "../../api";
import Spinner from "../../components/Spinner";
import {useConsumerContext} from "../../context/ConsumerContext";
import {AVAILABLE_ACTIONS} from "../../utils/constants";
import {MUIAutoComplete, MUIInput} from "../../utils/formUtils";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert} from "@material-ui/lab";
import {makeStyles} from "@material-ui/styles";
import GoogleMaps from "../../utils/GooglePlacesInput";

const imgStyle = makeStyles({
    root: {
        backgroundColor: '#007DCB'
    }
})

const validationSchema = yup.object().shape({
    addressLine1: yup.string().required('Address required.').nullable(),
    zip: yup.number().typeError('Zip must be a valid Florida code').required('Florida ZIP required.').positive('Zip must be a valid Florida code').integer().min(32003, 'Zip must be a valid Florida code').max(34997, 'Zip must be a valid Florida code'),
    city: yup.string().required('City required.').nullable(),
    county: yup.string().required('County required.').nullable(),
});
export default function AddrPicker() {
    // triggering the react hook form
    const {handleSubmit, control, formState: {errors}} = useForm({resolver: yupResolver(validationSchema)});

    // context from parent
    const {consumerData, dispatch} = useConsumerContext();

    // Image style
    const classes = imgStyle();

    // loading static data when component loads
    useEffect(() => {
        // Anything in here is fired on component mount.
        // fetching static data
        setCityList(addressData.city);
        setCountyList(addressData.county);
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"userRequestStatus": "INIT"}});
        // loading csrf as cookie so tht it can be used in next post
        axiosInstance.get('unauth/leads/csrf', {withCredentials: true})
    }, []);

    // state variables
    const [cityList, setCityList] = useState([]);
    const [countyList, setCountyList] = useState([]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [snackBarSettings, setSnackBarSettings] = useState({show:false, message : 'An error occurred. Please try later', variant:'error'});

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarSettings(Object.assign({},{show:false, message : 'An error occurred. Please try later', variant:'error'}));
    }

    // form submission
    const onSubmit = data => {
        setShowSpinner(true);
        data["grecaptcha"] = consumerData?.grecaptcha;
        if (consumerData?.userRequestId && "init" != consumerData.userRequestId) {
            updateUserRequest();
        } else {
            createUserRequest(data);
        }

    }

    const getAddressObject = (address_components) => {
        const ShouldBeComponent = {
            home: ["street_number"],
            postal_code: ["postal_code"],
            street: ["street_address", "route"],
            region: [
                "administrative_area_level_1",
                "administrative_area_level_2",
                "administrative_area_level_3",
                "administrative_area_level_4",
                "administrative_area_level_5"
            ],
            city: [
                "locality",
                "neighborhood",
                "sublocality",
                "sublocality_level_1",
                "sublocality_level_2",
                "sublocality_level_3",
                "sublocality_level_4"
            ],
            country: ["country"],
            county: [
                "administrative_area_level_2",
                "political"
            ]
        };

        const address = {
            home: "",
            postal_code: "",
            street: "",
            region: "",
            city: "",
            country: "",
            county: ""
        };
        address_components.forEach(component => {
            for (let shouldBe in ShouldBeComponent) {
                if (ShouldBeComponent[shouldBe].indexOf(component.types[0]) !== -1) {
                    if (shouldBe === "country") {
                        address[shouldBe] = component.short_name;
                    } else {
                        address[shouldBe] = component.long_name;
                    }
                }
            }
        });
        return address;
    }

    const populateValues = (data) => {
        //console.log(data[0].address_components)
        if( data && data[0] && data[0].address_components){
            const address =  getAddressObject(data[0].address_components)
            if(!address || address.region !== 'Florida'){
                setSnackBarSettings({show:true, message : 'Please choose an address in Florida', variant:'error'})
                return
            }
            //console.log('i will populate values here...', address)
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"addressLine1": address.home + address.street}});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"city": address.city}});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"zip": address.postal_code}});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"county": address.county}});
        }
    }

    const resetValues = () => {
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"addressLine1": ''}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"city": ''}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"zip": ''}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"county": ''}});
    }

    function updateUserRequest() {
        let payload = Object.assign({}, consumerData);
        delete payload.roof;
        delete payload.electrical;
        delete payload.plumbing;
        axiosInstance.post("unauth/leads/updateUserRequestInterm", payload).then(address => {
            dispatch({
                type: AVAILABLE_ACTIONS.UPDATE_ADDRESS,
                userRequest: {"fireStationPrefill": address?.fireStationPrefill}
            });
            dispatch({
                type: AVAILABLE_ACTIONS.UPDATE_ADDRESS,
                userRequest: {"distanceToCoastPrefill": address?.distanceToCoastPrefill}
            });
            dispatch({
                type: AVAILABLE_ACTIONS.UPDATE_ADDRESS,
                userRequest: {"milesFromSaltWater": address?.milesFromSaltWater}
            });
            dispatch({
                type: AVAILABLE_ACTIONS.UPDATE_ADDRESS,
                userRequest: {"is5miFromMannedFireStation": address?.is5miFromMannedFireStation}
            });
            dispatch({type: AVAILABLE_ACTIONS.MOVE_NEXT, wizardStep: consumerData.wizardStep + 1});
            setHasError(false);
        }).catch(e => {
            setHasError(true);
        }).finally(() => setShowSpinner(false));
    }

    function createUserRequest(data) {
        axiosInstance.post("unauth/leads/newUserRequest", data).then(address => {
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: address});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"insuranceReason": "none"}});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"propertyUsage": "none"}});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"propertyType": "none"}});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"constructionType": "none"}});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"numOfClaims": "-100"}});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"businessType": "none"}});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"roof": "none"}});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"electrical": "none"}});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"plumbing": "none"}});
            if (address && address.distanceToCoastPrefill !== 'Y') {
                dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"milesFromSaltWater": "-100"}});
            }
            dispatch({type: AVAILABLE_ACTIONS.MOVE_NEXT, wizardStep: consumerData.wizardStep + 1});
            setHasError(false);
        }).catch(e => {
            setHasError(true);
        }).finally(() => setShowSpinner(false));
    }

// updating the state as the user types...
    function handleFormChange(event) {
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {[event.target.name]: event.target.value}});
    }

// spinner
    if (showSpinner) return <Spinner/>;


    return (
        <section className="pageBackground">
            <div className="pageCenter">
                <Typography align="center" gutterBottom>
                    <p className="centerText"> Register a Property with <center className="lightBlue">FMAP</center></p>
                </Typography>
                <form className="pageForm"
                      onSubmit={handleSubmit(onSubmit)}>
                    <Paper style={{padding: 24}}>
                        <Grid container alignItems="flex-start" spacing={2}>
                            <Grid item xs={12} sm={12} md={6}>

                                <MUIInput control={control} name="addressLine1" inputPrp={{maxLength: 200}}
                                          defaultValue={consumerData?.addressLine1}
                                          value={consumerData?.addressLine1} label="Address Line 1"
                                          onInputChange={handleFormChange} errors={errors}/>


                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <MUIInput control={control} name="addressLine2" inputPrp={{maxLength: 200}}
                                          defaultValue={consumerData?.addressLine2}
                                          value={consumerData?.addressLine2} label="Address Line 2"
                                          onInputChange={handleFormChange} errors={errors}></MUIInput>

                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                {/*<MUISelect id="city" control={control} name="city" label="City" errors={errors}*/}
                                {/*           value={consumerData?.city}*/}
                                {/*           onFormChange={handleFormChange} required="true"*/}
                                {/*           defaultValue={consumerData?.city}>*/}
                                {/*    {cityList.map(city => {*/}
                                {/*        return <MenuItem key={city} value={city}>{city}</MenuItem>*/}
                                {/*    })}*/}
                                {/*</MUISelect>*/}
                                <MUIAutoComplete id="city" control={control} name="city" label="City" errors={errors}
                                                 value={consumerData?.city} onValueChange={handleFormChange}
                                                 options={cityList}/>

                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                {/*<MUISelect id="county" control={control} name="county" label="County" errors={errors}*/}
                                {/*           defaultValue={consumerData?.county}*/}
                                {/*           value={consumerData?.county} onFormChange={handleFormChange}>*/}
                                {/*    {countyList.map(county => {*/}
                                {/*        return <MenuItem key={county} value={county}>{county}</MenuItem>*/}
                                {/*    })}*/}
                                {/*</MUISelect>*/}
                                <MUIAutoComplete id="county" control={control} name="county" label="County"
                                                 errors={errors}
                                                 value={consumerData?.county} onValueChange={handleFormChange}
                                                 options={countyList}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <MUIInput control={control} name="zip" defaultValue={consumerData?.zip}
                                          inputPrp={{maxLength: 5}}
                                          value={consumerData?.zip} label="ZIP"
                                          error={consumerData?.errors?.zip}
                                          onInputChange={handleFormChange} errors={errors}></MUIInput>
                            </Grid>

                            {/* <Grid item xs={12} sm={6} md={4}>
                            <Controller
                                required
                                name="state"
                                control={control}
                                defaultValue="FL"
                                type="text"
                                label="State/ Country"
                                render={({field}) => <TextField fullWidth InputProps={{readOnly: true}} {...field}
                                                                value="FL"
                                                                variant="filled" label="State"/>}
                            />
                        </Grid> */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Box component="span">
                                    <Button type="submit" variant="contained" color="primary" name="btnNext"
                                            className="searchBtn">
                                        Next
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid container>

                            {/*<Grid item  xs={12} sm={12} md={12}>*/}
                            {/*    <Box component="span" justifyContent="flex-end" alignItems="flex-end" display="flex">*/}
                            {/*        <br/>*/}
                            {/*        <Button type="submit" variant="contained" color="primary" name="btnNext">*/}
                            {/*            Next*/}
                            {/*        </Button>*/}
                            {/*    </Box>*/}
                            {/*</Grid>*/}
                            <Grid item xs={12} sm={12} md={12}>
                                {hasError && <div>
                                    <br/>
                                    <Alert variant="filled" severity="error">An error occurred. Please try again
                                        later.</Alert>
                                </div>}
                            </Grid>
                        </Grid>
                    </Paper>


                </form>

            </div>
            <div className="homeBg"> &nbsp;</div>

            <Snackbar open={snackBarSettings.show} autoHideDuration={6000} onClose={handleSnackBarClose}>
                <Alert variant="filled" onClose={handleSnackBarClose}
                       severity={snackBarSettings.variant}>{snackBarSettings.message}</Alert>
            </Snackbar>
        </section>
    );
}

